/*
 * @Date: 2024-03-12 11:39:11
 * @LastEditors: Zhouqi
 * @LastEditTime: 2025-12-23 17:29:56
 * @FilePath: /video-marketing/utils/index.ts
 */
import 'dayjs/locale/zh-cn';

import crypto from 'crypto';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import cookie from 'js-cookie';

import { COOKIE_DOMAIN } from '@/config';
import { isCnArea } from '@/utils/envHelper';

import { formatTimestampToMDMS } from './common';
import {
  AGREEMENT_CONFIG,
  AudioGroupCodes,
  ListTypeValueType,
  ServerListType,
} from './const';

// 导入相对时间插件
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: '%d seconds',
    m: 'a minute',
    mm: '%d minutes',
    h: 'an hour',
    hh: '%d hours',
    d: 'a day',
    dd: '%d days',
    M: 'a month',
    MM: '%d months',
    y: 'a year',
    yy: '%d years',
  },
});

// 将十六进制转换为二进制
function fromHex(hex: string): Int8Array {
  const binary = new Int8Array(hex.length / 2);
  for (let i = 0; i < binary.length; i++) {
    binary[i] = parseInt(hex.substring(2 * i, 2 * i + 2), 16);
  }
  return binary;
}

// 使用 PBKDF2 加密密码并迭代指定次数
const hashPassword = (
  password: string,
  salt: Int8Array,
  iterations: number,
): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    crypto.pbkdf2(password, salt, iterations, 64, 'sha1', (err, derivedKey) => {
      if (err) {
        reject(err);
      } else {
        resolve(derivedKey.toString('hex'));
      }
    });
  });
};

// 登录密文生成
export const ciphertext = async (salt: string, password = '') => {
  let oldPassword = '';
  // 旧密码密文
  if (salt && password) {
    oldPassword = await hashPassword(password, fromHex(salt), 1000);
  }

  // 生成随机的盐值
  const saltRandom = crypto.randomBytes(8).toString('hex');
  // 新密码密文
  const newPassword = await hashPassword(password, fromHex(saltRandom), 1000);
  return {
    oldPassword,
    newPassword,
    salt: saltRandom,
  };
};

// 获取cookie信息
export const getCookie = (str: string) => JSON.parse(cookie.get(str) || '{}');

/**
 *
 * @param key
 * @param value
 * @param time 天
 * @description 设置cookie信息
 */
export const setCookie = (key: string, value: string | number, time = 7) => {
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + time);
  cookie.set(key, JSON.stringify(value), {
    expires: expirationDate,
    domain: COOKIE_DOMAIN,
  });
};

export const removeCookie = (key: string) => {
  cookie.remove(key, {
    domain: COOKIE_DOMAIN,
  });

  cookie.remove(key);
};

// 设置本地缓存
export const setSessionStorage = (key: string, value: any) => {
  sessionStorage.setItem(key, JSON.stringify(value));
};

// 移除本地缓存
export const removeSessionStorage = (key: string) => {
  sessionStorage.removeItem(key);
};

// 取出本地缓存
export const getSessionStorage = (key: string) => {
  const res = sessionStorage.getItem(key);
  try {
    return res ? JSON.parse(res) : null;
  } catch (error) {
    return null;
  }
};

// 手机号正则校验
export const phoneReg = (phone: string) => {
  const reg = /^1(3\d|4[5-9]|5[0-35-9]|6[567]|7[0-8]|8\d|9[0-35-9])\d{8}$/;
  return reg.test(phone);
};

export const isAllDigits = (str: string) => /^\d+$/.test(str);
export const isValidEmail = (email: string) =>
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

export const passwordReg = (password: string) => {
  const passwordRegex =
    /^(?![0-9]+$)(?![a-zA-Z]+$)(?![!@#$%^&*.]+$)[0-9A-Za-z!@#$%^&*.]{8,}$/;
  return passwordRegex.test(password);
};

// 过滤空对象
export const filterEmptyObj = (obj: Record<string, any>) => {
  return Object.keys(obj).reduce((pre: Record<string, any>, cur: string) => {
    return obj[cur] !== undefined && obj[cur] !== null
      ? { ...pre, [cur]: obj[cur] }
      : pre;
  }, {});
};

// 获取链接资源类型
export const getFileExtensionFromUrl = (url: string): string | null => {
  const parts = url.split('/');
  const lastPart = parts[parts.length - 1];
  const extensionMatch = lastPart.match(/\.(\w+)$/); // 使用正则表达式匹配扩展名
  return extensionMatch ? extensionMatch[1] : null;
};

// 下载图片
export const downloadImage = (imageUrl: string, name: string): void => {
  const image = new Image();
  image.setAttribute('crossOrigin', 'anonymous');
  image.onload = function (): void {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const context = canvas.getContext('2d') as CanvasRenderingContext2D;
    context.drawImage(image, 0, 0, image.width, image.height);
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    const event = new MouseEvent('click');
    a.download = name;
    a.href = url;
    a.dispatchEvent(event);
  };
  image.src = imageUrl;
};

// 下载音频
export const downloadAudio = (audioUrl: string, name: string): void => {
  const audio = new Audio(audioUrl);
  audio.crossOrigin = 'anonymous';
  audio.addEventListener('canplaythrough', async () => {
    const arrayBuffer = await fetch(audioUrl).then((response) => response.arrayBuffer());
    const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    // 清理资源
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  });

  audio.load();
};

/**
 * 下载视频方法
 * @param videoUrl 视频链接
 * @param fileName 下载的文件名
 */
export const downloadVideo = async (
  videoUrl: string,
  fileName: string,
): Promise<void> => {
  try {
    // 使用 fetch 获取视频数据
    const response = await fetch(videoUrl);

    // 将视频数据转换为 Blob
    const videoBlob = await response.blob();

    // 创建一个下载链接
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(videoBlob);

    // 设置下载链接的下载属性和文件名
    downloadLink.download = fileName;

    // 模拟点击下载链接
    document.body.appendChild(downloadLink);
    downloadLink.click();

    // 清理创建的元素
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(downloadLink.href);
  } catch (error) {
    console.error('视频下载失败', error);
  }
};

/**
 * 批量下载视频
 */
export const downloadVideos = async (
  videos: { url: string; name: string }[],
  delay: number = 50,
): Promise<void> => {
  for (const video of videos) {
    await downloadVideo(video.url, video.name);
    await new Promise((resolve) => setTimeout(resolve, delay));
  }
};
/**
 * 下载文件
 */
export const downloadFile = async (url: string, name: string) => {
  const link = document.createElement('a');
  link.addEventListener('click', (e) => {
    e.stopPropagation();
  });
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * 获取url参数
 * @param name 参数名
 * @returns
 */
export const getUrlParameter = (name: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

export const getCreateTime = (time: number) => {
  const timestamp = time; // 时间戳

  const currentTime = dayjs(); // 当前时间
  const targetTime = dayjs(timestamp); // 目标时间

  const hoursAgo = currentTime.diff(targetTime, 'hour'); // 计算当前时间与目标时间的小时差
  const minuteAgo = currentTime.diff(targetTime, 'minute');
  const secondAgo = currentTime.diff(targetTime, 'second');

  let formattedTime;
  dayjs.locale(window.navigator.language.includes('zh') ? 'zh-cn' : 'en');

  if (secondAgo < 10) {
    formattedTime = window.navigator.language.includes('zh') ? '刚刚' : 'just now';
  } else if (secondAgo < 60) {
    formattedTime = dayjs().subtract(secondAgo, 'second').from(currentTime);
  } else if (minuteAgo < 60) {
    formattedTime = dayjs().subtract(minuteAgo, 'minute').from(currentTime);
  } else if (hoursAgo < 24) {
    formattedTime = dayjs().subtract(hoursAgo, 'hour').from(currentTime); // 格式化为 "几小时前" 的形式
  } else {
    formattedTime = dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  }

  return formattedTime;
};

/**
 * 时长展示
 * @param duration 192秒 or 192000
 * @param milliseconds true=毫秒
 * @returns 00:12
 */

// export const formatDuration = (time: number, millisecond?: boolean): string => {
//   let duration = time;
//   if (!duration) return '00:00';
//   if (millisecond) {
//     duration = duration / 1000;
//   }
//   let seconds = Math.floor(duration % 60);
//   let minutes = Math.floor((duration - seconds) / 60);

//   let mm = minutes.toString();
//   let ss = seconds.toString();
//   if (minutes < 10) mm = `0${mm}`;
//   if (seconds < 10) ss = `0${ss}`;
//   return `${mm}:${ss}`;
// };

export const formatDuration = (
  duration: number,
  milliseconds: boolean = true,
): string => {
  if (!duration) return '00:00';
  // 如果是毫秒，将其转换为秒
  const totalSeconds = milliseconds ? Math.floor(duration / 1000) : Math.floor(duration);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const minuteString = minutes.toString().padStart(2, '0');
  const secondString = seconds.toString().padStart(2, '0');

  if (hours > 0) {
    const hourString = hours.toString().padStart(2, '0');
    return `${hourString}:${minuteString}:${secondString}`;
  } else {
    return `${minuteString}:${secondString}`;
  }
};

/**
 * 将时间秒数转化为 分钟:秒数展示 eg 02:30
 * @param milliseconds
 * @returns
 */
const formatTimestamp = (milliseconds: number): string => {
  if (!milliseconds) return '00:00';
  const seconds = milliseconds / 1000;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
};

export const dealVideoPreviewUrl = (url: string) => {
  if (!url || !url.startsWith('http')) return '';
  return url;
};

export const getAgreementUrl = () => {
  return AGREEMENT_CONFIG['oversea'];
};

export const getAudioPackageCode = (modelKey: string): string => {
  if (isCnArea) {
    return AudioGroupCodes['cn'][modelKey];
  } else {
    return AudioGroupCodes['oversea'][modelKey];
  }
};

export const isUrl = (str: string) => {
  const pattern = new RegExp(
    // @ts-ignore
    '^(https?:\\/\\/)?' ||
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*\\.)+[a-z]{2,}|' ||
      '((\\d{1,3}\\.){3}\\d{1,3}))' ||
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' ||
      '(\\?[;&a-z\\d%_.~+=-]*)?' ||
      '(\\#[-a-z\\d_]*)?$' ||
      'i',
  );
  return !!pattern.test(str);
};

export const formatTitle = ({
  customType,
  createTime,
}: {
  customType: ListTypeValueType;
  createTime: number;
}): string => {
  switch (Number(customType)) {
    case ServerListType.CAPTION_CLEAROFF:
      return 'Removal_' + formatTimestampToMDMS(createTime);
    case ServerListType.VIDEO_SUBMIT_EXPORT:
      return 'Video_' + formatTimestampToMDMS(createTime);
    case ServerListType.LIP_SYNC:
      return 'LipSync_' + formatTimestampToMDMS(createTime);
    case ServerListType.BATCH_VIDEO_TRANS:
    case ServerListType.BATCH_VIDEO_TRANS_RESUBMIT:
      return 'Translation_' + formatTimestampToMDMS(createTime);
    case ServerListType.FACE_MERGE:
      return 'FaceSwapper_' + formatTimestampToMDMS(createTime);
    default:
      return '';
  }
};

/**
 * 时长向上取整一位整数
 * @param milliseconds
 * @returns
 */
export const durationMathCeil = (milliseconds: number | undefined): number => {
  if (!milliseconds) return 0;
  return Math.ceil((milliseconds / 1000) * 10) / 10;
};

export const delayFn = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export const formatDate = (timestamp: number) => {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}.${month}.${day} ${hours}:${minutes}:${seconds}`;
};
